// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { MappedLink } from "@insite/mobius/Menu";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import { boxStyles } from "@store/Themes/common/mixins/box";
import { linkStyles } from "@store/Themes/common/mixins/link";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import * as React from "react";
import { createRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

// Kishigo --->
import { contentSpacingStyles } from "@store/Themes/common/mixins/spacing";
import {
    sideMenuLinkActiveStyles,
    sideMenuLinkStyles,
    sideMenuWrapperStyles,
} from "@store/Themes/kishigo/mixins/sidemenu";
import useMergeProps from "@toolkit/Framework/Common/Hooks/useMergeProps";
import overrideProps from "@toolkit/Framework/Common/Utilities/overrideProps";
import shadows from "./../../../Themes/kishigo/styleguide/config/shadows";
// <--- Kishigo

export interface SideMenuPresentationProps extends ExpandStyles<SideMenuStyles> {}

export type SideMenuLinkPresentationProps = LinkPresentationProps & {
    isCurrent?: boolean;
};

export interface SideMenuStyles {
    wrapper?: InjectableCss;
    skipNav?: SkipNavStyles;
    list?: InjectableCss;
    listItem?: InjectableCss;
    link?: SideMenuLinkPresentationProps;
    signOutLink?: SideMenuLinkPresentationProps;
}

// Kishigo --->
const _linkStyles = () => css<SideMenuLinkPresentationProps>`
    ${sideMenuLinkStyles()}

    ${({ isCurrent }) =>
        isCurrent &&
        css`
            ${sideMenuLinkActiveStyles()}
        `}
`;

export const sideMenuStyles: SideMenuStyles = {
    wrapper: {
        css: css`
            ${sideMenuWrapperStyles(`${pxToRem(5)} 0`)}
            box-shadow: ${x => x.theme.shadows.e100};
        `,
    },
    list: {},
    listItem: {
        css: contentSpacingStyles(0, 0, pxToRem(5)),
    },
    link: {
        css: _linkStyles(),
    },
    signOutLink: {
        css: css<SideMenuLinkPresentationProps>`
            ${_linkStyles()}
            color: ${getColor("primary")};
        `,
    },
};
// <--- Kishigo

const List = styled.ul`
    ${injectCss}
`;
const ListItem = styled.li`
    ${injectCss}
`;
const StyledLink = styled(Link)<SideMenuLinkPresentationProps>``;

export interface SideMenuProps extends SideMenuPresentationProps {
    pageType?: string;
    nodeId?: string;
    showRootPage?: boolean;
    showSignOut?: boolean;
    onClick?: React.EventHandler<React.MouseEvent>;
    excludedPageTypes?: string[];
    links?: (MappedLink | PageLinkModel)[];
}

const mapStateToProps = (state: ApplicationState, props: SideMenuProps) => ({
    rootPageLink: props.pageType
        ? getPageLinkByPageType(state, props.pageType)
        : getPageLinkByNodeId(state, props.nodeId || getCurrentPage(state).nodeId),
    currentLocation: getLocation(state),
    isSignedIn: state.context.session.userName && !getCurrentUserIsGuest(state),
});

const mapDispatchToProps = {
    signOut,
};

type Props = SideMenuProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const SideMenu: React.FunctionComponent<Props> = ({
    showRootPage,
    showSignOut,
    overridedStyles,
    extendedStyles,
    onClick,
    rootPageLink,
    currentLocation,
    isSignedIn,
    signOut,
    excludedPageTypes = [],
    links: baseLinks,
}) => {
    const styles = useExpandProps(sideMenuStyles, extendedStyles, overridedStyles);
    const afterMenu = createRef<HTMLSpanElement>();
    const links = (baseLinks?.length ? baseLinks : rootPageLink?.children) as
        | (MappedLink & PageLinkModel)[]
        | undefined;

    if (!links?.length && !showSignOut) {
        return null;
    }

    const currentPageUrl = currentLocation.pathname.toLowerCase();
    const _excludedPageTypes = [...excludedPageTypes];

    const _links = links?.filter(x => !x.excludeFromNavigation && !_excludedPageTypes.some(y => x.type === y));

    return (
        <StyledWrapper as="nav" {...styles.wrapper}>
            <SkipNav destination={afterMenu} extendedStyles={styles.skipNav} text={translate("Skip Navigation")} />
            <List {...styles.list}>
                {showRootPage && rootPageLink && (
                    <ListItem {...styles.listItem}>
                        <StyledLink
                            href={rootPageLink.url}
                            isCurrent={currentPageUrl === rootPageLink.url.toLowerCase()}
                            onClick={onClick}
                            {...styles.link}
                        >
                            {rootPageLink.title}
                        </StyledLink>
                    </ListItem>
                )}

                {_links?.map((link, index) => (
                    <ListItem key={link.id || `${link.title}_${index}`} {...styles.listItem}>
                        <StyledLink
                            href={link.url}
                            isCurrent={Boolean(link.url) && currentPageUrl.startsWith(link.url.toLowerCase())}
                            onClick={(event: React.MouseEvent) => {
                                link.clickableProps?.onClick?.(event);
                                onClick?.(event);
                            }}
                            icon={{ iconProps: link.iconProps }}
                            {...styles.link}
                        >
                            {link.title}
                        </StyledLink>
                    </ListItem>
                ))}

                {isSignedIn && showSignOut && (
                    <ListItem {...styles.listItem}>
                        <StyledLink
                            onClick={(event: React.MouseEvent) => {
                                signOut();
                                onClick?.(event);
                            }}
                            {...(styles.signOutLink ? styles.signOutLink : styles.link)}
                        >
                            {translate("Sign Out")}
                        </StyledLink>
                    </ListItem>
                )}
            </List>
            <span ref={afterMenu} tabIndex={-1} />
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SideMenu);
